'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';

import { dur, ease, spring, springFade } from '@/lib/motion';

/**
 * ORTADA AÇILAN KATMAN — kenardan gelen panelin (`DashSheet`) kardeşi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden ikinci bir kabuk var
 * ═══════════════════════════════════════════════════════════════════════════
 * `DashSheet` kenardan giren DAR bir sütun (~520px) ve tek bir kararın
 * düzenlendiği yüzeyler için doğru: arkadaki ekran görünür kalıyor, kullanıcı
 * neyi düzenlediğini görüyor.
 *
 * Bazı katmanlar ise KARŞILAŞTIRMA yüzeyi — yan yana duran iki şey ya da çok
 * sütunlu bir ızgara. Dar bir sütunda ikisi alt alta düşer ve katmanın tek
 * işi kaybolur: örnek çıktıda "girdi → çıktı" okuması, şablon kataloğunda ise
 * kartları yan yana tarayabilmek. Onlar ortada ve geniş açılıyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Neden ORTAK bir bileşen — üçüncü kopyada yazıldı
 * ═══════════════════════════════════════════════════════════════════════════
 * Aynı mekanik üç yerde elle yazılmıştı: `editor-dryrun.tsx`, örnek video
 * katmanı ve şablon kataloğu. Odak tuzağı + Esc + kaydırma kilidi + odak
 * dönüşü — dördü de sessiz hatalar üretir ("Esc çalışmıyor" diye kimse
 * bildirmez) ve biri düzeltilip diğerleri unutulur. `DashSheet`in başlığında
 * aynı gerekçe yazılı; bu onun ortada duran hâli.
 *
 * `editor-dryrun.tsx` BİLEREK dokunulmadan bırakıldı: kendi gövdesini kendi
 * çiziyor ve buraya taşımak görsel bir riskti. Yeni katmanlar buradan doğsun.
 *
 * ⚠️ Başlık, gövde ve alt şerit ÇAĞIRANIN: bu kabuk yalnız kutuyu, perdeyi ve
 * mekaniği veriyor. İçeriği parametreleştirseydik (başlık + lead + footer
 * propları) iki çağıranın ikisine de tam uymayan bir soyutlama çıkardı —
 * biri sticky bir başlık istiyor, diğeri kategori şeridi taşıyan bir başlık.
 */
export function CenterLayer({
  open,
  onClose,
  labelledBy,
  className = 'dash-clay',
  layerClassName = 'dash-modal-layer',
  scrimClassName = 'dash-modal-scrim',
  animateResize = false,
  children,
}: {
  open: boolean;
  /**
   * Katmanı kapatma İSTEĞİ — Esc ve perdeye tıklama buraya düşüyor.
   *
   * "Kapat" değil "istek": çağıran kapatmamayı da seçebilir. Çerez duvarı
   * (`cookie-consent.tsx`) tam olarak bunu yapıyor — karar verilmeden Esc de
   * perde de kapatmıyor, yalnız açık ayar paneli soruya geri dönüyor.
   *
   * 🔴 Kimliği SABİT tutun (`useCallback`, boş bağımlılık + ref). Aşağıdaki
   * odak etkisi bu geri çağırmaya bağlı: her render'da yenilenirse temizlik
   * çalışıp odak açana geri döner, sonra kutuya geri gelir — kullanıcı
   * katmanın içinde bir düğmeye basınca odağı kaybeder.
   */
  onClose: () => void;
  /** Kutunun erişilebilir adını taşıyan başlığın `id`'si. */
  labelledBy: string;
  /**
   * Kutunun deri sınıf(lar)ı — genişlik ve iç düzen orada.
   *
   * Varsayılan `dash-clay` kendi ölçüsünü taşıyan sade bir kutu. Çağıran
   * başka bir deri verebiliyor ve şablon kataloğu tam olarak bunu yapıyor:
   * görsel şablonlarının katmanıyla BİREBİR aynı kutuyu (`dash-gal dash-wz`)
   * giyiyor — iki katalog aynı soruyu soruyor ve farklı görünmeleri,
   * kullanıcının öğrendiği bir refleksi kırardı.
   */
  className?: string;
  /**
   * Perdeyi ve ortalamayı yapan dış kap.
   *
   * `dash-modal-layer` ile `dash-gal-layer` aynı işi yapıyor (flex + center +
   * fixed) ama dolguları ve yığın sıraları farklı; galeri katmanı 95, modal
   * 90. Çağıran hangi ailenin parçası olduğunu söylüyor.
   */
  layerClassName?: string;
  /**
   * Perdenin sınıfı. Varsayılan panel derisi; panel DIŞINDA açılan bir katman
   * kendi perdesini vermek zorunda, çünkü `dash-modal-scrim` yalnız
   * `dashboard.css`te tanımlı ve o dosya /dashboard altında yükleniyor.
   */
  scrimClassName?: string;
  /**
   * Kutunun BOYU içerikle değişiyorsa açın: Motion `layout` ile boyu
   * transform'a çevirip animasyonluyor (DESIGN.md §1 — `height` animasyonu
   * her karede düzen hesabı demek).
   *
   * Varsayılan kapalı: mevcut üç çağıranın içeriği kategori değişiminde
   * yeniden doluyor ve onlara sonradan hareket eklemek görsel bir değişiklik
   * olurdu. Yeni katmanlar isteyerek açsın.
   */
  animateResize?: boolean;
  children: ReactNode;
}) {
  const boxRef = useRef<HTMLDivElement>(null);

  /* Portal yalnız tarayıcıda: `document` sunucuda yok. */
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return;
    /* Önceki değer geri yükleniyor — üst üste açılan iki katmanda `''` yazmak,
       alttaki hâlâ açıkken kilidi kaldırırdı (`DashSheet` ile aynı kural). */
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  /**
   * 🔴 `onClose` REF'te — odak efektinin bağımlılığı OLAMAZ.
   *
   * Çağıranlar `onClose={() => setOpen(false)}` yazıyor ve o fonksiyon her
   * çizimde yeni. Efekt ona bağlıyken, katmanın içindeki bir kutuya yazılan
   * her karakterde efekt sökülüp yeniden kuruluyor ve `boxRef.current?.focus()`
   * odağı kutudan alıyordu — kullanıcı tek harf yazıp odağını kaybediyor.
   * Fatura formunda ölçüldü (`automation-dialog.tsx`); aynı kalıp burada da
   * vardı ve durumu yukarıda tutan her çağıranda patlardı.
   */
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    boxRef.current?.focus();

    /**
     * Odaklanabilirler HER Tab'da yeniden okunuyor, bir kez değil: katmanın
     * içeriği değişebiliyor (kategori değişince ızgara yeniden doluyor) ve
     * açılışta alınmış bir liste sonradan gelen düğmeleri tanımazdı — tuzak
     * kullanıcıyı katmanın dışına bırakırdı.
     */
    const focusables = () =>
      Array.from(
        boxRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), video, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((element) => element.offsetWidth > 0 || element.offsetHeight > 0);

    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        /* `stopPropagation`: altta açık bir katman varsa (kapının kendisi bir
           panelin içinde olabilir) Esc yalnız en üsttekini kapatmalı. */
        event.stopPropagation();
        closeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const list = focusables();
      const head = list[0];
      const tail = list[list.length - 1];
      if (!head || !tail) return;

      if (event.shiftKey && document.activeElement === head) {
        event.preventDefault();
        tail.focus();
      } else if (!event.shiftKey && document.activeElement === tail) {
        event.preventDefault();
        head.focus();
      }
    }

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus();
    };
  }, [open]);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <div className={layerClassName}>
          <motion.button
            type="button"
            className={scrimClassName}
            tabIndex={-1}
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: dur.fast, ease: ease.out }}
            onClick={onClose}
          />

          {/* Kutu OLDUĞU YERDE beliriyor, kenardan gelmiyor: ortada açılan bir
              katmanın "nereden geldiği" diye bir cevabı yok ve 16px'lik bir
              yükselme, perdeyle birlikte okunduğunda "üste çıktı" diyor.
              Çıkış daha kısa ve yaysız — kapanan bir şey beklemez. */}
          <motion.div
            ref={boxRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelledBy}
            tabIndex={-1}
            className={className}
            layout={animateResize}
            initial={{ opacity: 0, y: 16, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, transition: { duration: dur.instant } }}
            transition={springFade(spring.soft)}
          >
            {children}
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
